import React, { useState, useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useLocation } from 'react-router-dom';
import axios from 'axios';
import { Camera, ChevronLeft, ChevronRight, ChevronDown, Video, Search, GripVertical, LayoutGrid, Maximize2, Minimize2, Eraser, AlertTriangle, Volume2 } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useStreamingMode } from '../contexts/StreamingModeContext';
import VideoCard from '../components/VideoCard';
import XTAMSpinner from '../components/XTAMSpinner';
import { useStreamWebSocket } from '../hooks/useStreamWebSocket';
import { siteOperationalUi, streamCameraOperationalUi } from '../data/inventoryCameraOperational';
import './StreamsPage.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3009/api/v1';
const VIDEO_ENGINE_URL = process.env.REACT_APP_VIDEO_ENGINE_URL || 'http://localhost:9090';

/**
 * Máximo de arranques de stream simultáneos al llenar la matriz (arrastrar un
 * sitio o restaurar la grilla). Antes se escalonaban 600 ms entre cada arranque,
 * lo que hacía que los slots cargaran de a uno (perceptible sobre todo en 16
 * slots con cámaras ya precargadas). Ahora se arrancan en paralelo para que
 * carguen de forma prácticamente simultánea; el tope solo evita un pico de pulls
 * RTSP en frío que podría saturar MediaMTX. Configurable vía env; 0/negativo = sin límite.
 */
const STREAM_START_MAX_CONCURRENCY = (() => {
  const parsed = Number(process.env.REACT_APP_STREAM_START_MAX_CONCURRENCY);
  return Number.isFinite(parsed) && parsed > 0 ? Math.floor(parsed) : 8;
})();

function pickLiveStreamUrl(preferWebrtc: boolean, streamUrls: { hls?: string; webrtc?: string; rtsp?: string }): string | undefined {
  if (preferWebrtc) {
    return streamUrls.webrtc || streamUrls.hls || streamUrls.rtsp;
  }
  return streamUrls.hls || streamUrls.webrtc || streamUrls.rtsp;
}

interface Camera {
  id: string;
  name: string;
  description?: string;
  /** Semáforo operativo desde ``GET /cameras`` (``on`` / ``off`` / ``issue`` / ``unknown``). */
  operational?: 'on' | 'off' | 'issue' | 'unknown';
  status: string;
  streamUrl?: string;
  rtspUrl?: string;
  rtspUsername?: string;
  rtspPassword?: string;
  isOnvif?: boolean;
  /** true si la cámara tiene una bocina montada en su mismo poste (devices_cameras.siren_device_id). */
  hasSiren?: boolean;
  /** Coordenadas del dispositivo (API GET /cameras); para ordenar por cercanía a la alerta del botón */
  siteAddress?: string;
  latitude?: number;
  longitude?: number;
  streams?: {
    rtsp?: string;
    hls?: string;
    webrtc?: string;
  };
  site?: {
    id: string;
    name: string;
    address?: string;
  };
  deviceCamera?: {
    connectivityOnline?: boolean | null;
    lastConnectivityStatus?: boolean | null;
    probeState?: number | null;
    lastHeartbeatAt?: string | null;
  } | null;
}

/**
 * Arranca varias cámaras en paralelo para que los slots carguen de forma
 * simultánea (sin el retardo perceptible slot-a-slot del escalonamiento anterior).
 * Se limita a STREAM_START_MAX_CONCURRENCY arranques a la vez para no saturar
 * MediaMTX con un pico de pulls RTSP en arranques en frío; conforme cada arranque
 * termina, entra el siguiente. Resuelve cuando todos han terminado.
 */
function startStreamsInParallel(
  jobs: { camera: Camera; index: number }[],
  startFn: (camera: Camera, index: number) => Promise<void>,
): Promise<void> {
  if (jobs.length === 0) return Promise.resolve();

  const limit =
    STREAM_START_MAX_CONCURRENCY > 0
      ? Math.min(STREAM_START_MAX_CONCURRENCY, jobs.length)
      : jobs.length;

  let cursor = 0;
  const runNext = async (): Promise<void> => {
    const current = cursor++;
    if (current >= jobs.length) return;
    const { camera, index } = jobs[current];
    await startFn(camera, index).catch(() => {});
    await runNext();
  };

  return Promise.all(Array.from({ length: limit }, () => runNext())).then(() => undefined);
}

/** Distancia aproximada en km (orden relativo de cámaras respecto al punto de la alerta). */
function haversineKm(lat1: number, lon1: number, lat2: number, lon2: number): number {
  const R = 6371;
  const toRad = (d: number) => (d * Math.PI) / 180;
  const dLat = toRad(lat2 - lat1);
  const dLon = toRad(lon2 - lon1);
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) ** 2;
  return R * (2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a)));
}

function distanceCameraToAlert(cam: Camera, alertLat: number, alertLng: number): number {
  const lat = cam.latitude;
  const lng = cam.longitude;
  if (lat == null || lng == null || !Number.isFinite(lat) || !Number.isFinite(lng)) {
    return Number.POSITIVE_INFINITY;
  }
  return haversineKm(alertLat, alertLng, lat, lng);
}

/** Prioriza cámaras más cercanas al punto del botón/alarma dentro del mismo sitio. */
function sortCamerasByAlertProximity(cameras: Camera[], alertLat: number, alertLng: number): Camera[] {
  return [...cameras].sort(
    (a, b) => distanceCameraToAlert(a, alertLat, alertLng) - distanceCameraToAlert(b, alertLat, alertLng)
  );
}

interface Site {
  id: string;
  name: string;
  address?: string;
  serviceName?: string; // PRIVADO (1), EU (2), PVVC (3), XTAM MASIVOS (4)
  /** Semáforo agregado (``sites.state_id``: 1=ON, 2=novedad, 3=mantenimiento, 4=OFF). */
  stateId?: number;
  /** Si false, no corta el stream por tiempo (PRIVADO/EU/MASIVOS). */
  streamingOnDemandEnabled?: boolean;
}

interface TestStream {
  cameraId: string;
  name: string;
  status: string;
  hlsUrl?: string;
  error?: string;
}

export type GridLayoutType = 1 | 2 | '3x2' | 3 | '4x3' | 4 | 'surveillance';
const SURVEILLANCE_SLOTS = 8;

function getTotalSlots(layout: GridLayoutType): number {
  if (layout === 'surveillance') return SURVEILLANCE_SLOTS;
  if (layout === '3x2') return 6;
  if (layout === '4x3') return 12;
  return (layout as number) * (layout as number);
}

const STREAMS_GRID_STORAGE_KEY = 'vms_streams_grid';

/** Minutos antes de pausar streaming bajo demanda (sitios Privado y EU). Config: REACT_APP_STREAMING_ON_DEMAND_PRIVADOS_EU_MINUTES en frontend/.env (default 5 si se omite). */
function readOnDemandPrivadosEuTimeoutMs(): number {
  const raw = process.env.REACT_APP_STREAMING_ON_DEMAND_PRIVADOS_EU_MINUTES;
  const minutes = parseInt(String(raw ?? '').trim(), 10);
  const clamped =
    Number.isFinite(minutes) && minutes > 0 ? Math.min(minutes, 24 * 60) : 5;
  return clamped * 60 * 1000;
}

const ON_DEMAND_TIMEOUT_MS = readOnDemandPrivadosEuTimeoutMs();

const StreamsPage: React.FC = () => {
  const { token } = useAuth();
  const location = useLocation();
  const { mode: streamingMode, setMode: setStreamingMode } = useStreamingMode();
  // Solo WebRTC en esta pantalla; forzar al montar (sin selector en UI).
  useEffect(() => {
    setStreamingMode('webrtc');
  }, [setStreamingMode]);

  const [allCameras, setAllCameras] = useState<Camera[]>([]); // Todas las cámaras del sistema
  const [gridCameras, setGridCameras] = useState<(Camera | null)[]>([]); // Cámaras en la grilla
  const [sites, setSites] = useState<Site[]>([]);
  const [filteredCameras, setFilteredCameras] = useState<Camera[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedSite, setSelectedSite] = useState<string>('');
  const [gridLayout, setGridLayout] = useState<GridLayoutType>(2);
  const [showCameraBanner, setShowCameraBanner] = useState(false);
  const [loading, setLoading] = useState(false);
  const [draggedCamera, setDraggedCamera] = useState<Camera | null>(null);
  const [draggedFromIndex, setDraggedFromIndex] = useState<number | null>(null);
  const [draggedSite, setDraggedSite] = useState<{ site: Site; cameras: Camera[] } | null>(null);
  const [dragSource, setDragSource] = useState<'list' | 'grid' | 'site' | null>(null);
  const [selectedCameras, setSelectedCameras] = useState<string[]>([]);
  const [testStreams, setTestStreams] = useState<TestStream[]>([]);
  const [startingTestStreams, setStartingTestStreams] = useState(false);
  const [streamStatuses, setStreamStatuses] = useState<Map<string, { status: string; reconnectAttempts: number; isReconnecting: boolean }>>(new Map());
  const [isSchemaSidebarCollapsed, setIsSchemaSidebarCollapsed] = useState(true);
  const [isDraggingFromSchema, setIsDraggingFromSchema] = useState(false);
  const [schemaSearchQuery, setSchemaSearchQuery] = useState('');
  const [isStreamingFullscreen, setIsStreamingFullscreen] = useState(false);
  /** Slot mostrado en vista 1x1 (doble clic en slot). null = grilla normal. */
  const [focusedSlotIndex, setFocusedSlotIndex] = useState<number | null>(null);

  // Maximizar streaming: ocultar sidebar y header para usar 100% del viewport
  useEffect(() => {
    const FULLSCREEN_BODY_CLASS = 'streaming-fullscreen';
    if (isStreamingFullscreen) {
      document.body.classList.add(FULLSCREEN_BODY_CLASS);
    } else {
      document.body.classList.remove(FULLSCREEN_BODY_CLASS);
    }
    return () => {
      document.body.classList.remove(FULLSCREEN_BODY_CLASS);
    };
  }, [isStreamingFullscreen]);

  const [expandedServices, setExpandedServices] = useState<Record<string, boolean>>({
    PVVC: true,
    Privados: true,
    EU: true,
    Masivos: true,
  });
  const [expandedSites, setExpandedSites] = useState<Record<string, boolean>>({});

  /** Evita duplicar autofill solo si es la misma navegación; cada alerta nueva lleva mapSessionId distinto */
  const lastMapAutofillKeyRef = useRef<string | null>(null);
  /** Invalida restauración desde localStorage si el usuario vuelve a entrar con contexto de alerta / mapa */
  const gridRestoreGenerationRef = useRef(0);
  /** Tras auto-fill desde alerta, `replaceState` vacía `location.state`; evita que el efecto de restauración vuelva a aplicar caché. */
  const suppressNextGridRestoreRef = useRef(false);
  // Ref para no persistir en localStorage hasta terminar la carga inicial (o restauración)
  const gridInitializedRef = useRef(false);
  const gridLayoutRef = useRef(gridLayout);
  const gridCamerasRef = useRef<(Camera | null)[]>(gridCameras);
  gridLayoutRef.current = gridLayout;
  gridCamerasRef.current = gridCameras;

  // Bajo demanda: timestamp de cuándo se colocó cada cámara en el slot (NO se reinicia con reconexión)
  const slotPlacedAtRef = useRef<Map<number, number>>(new Map());
  interface FrozenSlot { camera: Camera; screenshot: string }
  const [frozenSlots, setFrozenSlots] = useState<Map<number, FrozenSlot>>(new Map());

  const renderLayoutIcon = (cols: number, rows: number) => {
    return (
      <span className={`layout-icon layout-icon-${cols}x${rows}`} aria-hidden="true">
        {Array.from({ length: cols * rows }).map((_, i) => (
          <span key={i} className="layout-icon-cell" />
        ))}
      </span>
    );
  };

  const renderSurveillanceLayoutIcon = () => (
    <span className="layout-icon-surveillance-wrap" aria-hidden="true">
      <span className="layout-icon layout-icon-surveillance">
        <span className="layout-icon-panel layout-icon-surv-main" />
        <span className="layout-icon-panel layout-icon-surv-stack" />
        <span className="layout-icon-panel layout-icon-surv-stack" />
        <span className="layout-icon-panel layout-icon-surv-stack" />
        <span className="layout-icon-panel layout-icon-surv-bottom" />
        <span className="layout-icon-panel layout-icon-surv-bottom" />
        <span className="layout-icon-panel layout-icon-surv-bottom" />
        <span className="layout-icon-panel layout-icon-surv-bottom" />
      </span>
    </span>
  );

  // Cargar sitios/cámaras cuando el sidebar de esquema esté visible (estado operativo actualizado)
  useEffect(() => {
    if (isSchemaSidebarCollapsed) return;
    if (!token) return;
    loadSites();
    // Refrescar siempre al abrir: el semáforo no debe pintarse con un snapshot viejo.
    loadCameras(true);
    const pollId = window.setInterval(() => loadCameras(true), 60_000);
    return () => window.clearInterval(pollId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSchemaSidebarCollapsed, token]);

  const schemaTree = React.useMemo(() => {
    const sitesById = new Map<string, Site>();
    sites.forEach((s) => sitesById.set(String(s.id), s));

    const camerasBySite = new Map<string, Camera[]>();
    allCameras.forEach((cam) => {
      const sid = cam.site?.id ? String(cam.site.id) : '';
      if (!sid) return;
      const list = camerasBySite.get(sid) || [];
      list.push(cam);
      camerasBySite.set(sid, list);
    });

    camerasBySite.forEach((list) => {
      list.sort((a, b) => String(a.name).localeCompare(String(b.name), undefined, { numeric: true }));
    });

    // Padres: PVVC | Privados | EU | Masivos (XTAM MASIVOS)
    type ServiceKey = 'PVVC' | 'Privados' | 'EU' | 'Masivos';
    const buckets: Record<ServiceKey, { sites: Site[] }> = {
      PVVC: { sites: [] },
      Privados: { sites: [] },
      EU: { sites: [] },
      Masivos: { sites: [] },
    };

    sites.forEach((s) => {
      const name = (s.serviceName || '').toUpperCase();
      if (name === 'PVVC') buckets.PVVC.sites.push(s);
      else if (name.includes('MASIV')) buckets.Masivos.sites.push(s);
      else if (name === 'PRIVADO' || name === 'PRIVADOS') buckets.Privados.sites.push(s);
      else if (name === 'EU') buckets.EU.sites.push(s);
      // Sin servicio o servicio desconocido: igual que en Grabaciones, va a Privados para no ocultarlo
      else buckets.Privados.sites.push(s);
    });

    // Orden estable
    (Object.keys(buckets) as ServiceKey[]).forEach((k) => {
      buckets[k].sites.sort((a, b) => String(a.name).localeCompare(String(b.name)));
    });

    return { buckets, camerasBySite, sitesById };
  }, [sites, allCameras]);

  const isOnDemandCamera = useCallback((camera: Camera | null): boolean => {
    if (!camera?.site?.id) return false;
    const site = schemaTree.sitesById.get(String(camera.site.id));
    if (!site?.serviceName) return false;
    if (site.streamingOnDemandEnabled === false) return false;
    const svc = site.serviceName.toUpperCase();
    return svc === 'PRIVADO' || svc === 'PRIVADOS' || svc === 'EU' || svc.includes('MASIV');
  }, [schemaTree.sitesById]);

  const markSlotPlaced = useCallback((slotIndex: number) => {
    if (!slotPlacedAtRef.current.has(slotIndex)) {
      slotPlacedAtRef.current.set(slotIndex, Date.now());
    }
  }, []);

  const clearSlotTimer = useCallback((slotIndex: number) => {
    slotPlacedAtRef.current.delete(slotIndex);
    setFrozenSlots(prev => {
      if (!prev.has(slotIndex)) return prev;
      const next = new Map(prev);
      next.delete(slotIndex);
      return next;
    });
  }, []);

  const captureVideoFrame = useCallback((slotIndex: number): string | null => {
    const slotEl = document.querySelector(`[data-slot-index="${slotIndex}"]`);
    if (!slotEl) return null;
    const video = slotEl.querySelector('video');
    if (!video || video.videoWidth === 0) return null;
    const canvas = document.createElement('canvas');
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const ctx = canvas.getContext('2d');
    if (!ctx) return null;
    ctx.drawImage(video, 0, 0);
    return canvas.toDataURL('image/jpeg', 0.85);
  }, []);

  const freezeSlot = useCallback((slotIndex: number) => {
    const cam = gridCameras[slotIndex];
    if (!cam) return;
    const screenshot = captureVideoFrame(slotIndex) || '';
    setFrozenSlots(prev => {
      const next = new Map(prev);
      next.set(slotIndex, { camera: cam, screenshot });
      return next;
    });
    // Actualización funcional para que varios slots que expiren en el mismo tick no pisen el estado.
    setGridCameras((prev) => {
      const next = [...prev];
      if (slotIndex < next.length) next[slotIndex] = null;
      return next;
    });
    setStreamStatuses(p => { const m = new Map(p); m.delete(cam.id); return m; });
    slotPlacedAtRef.current.delete(slotIndex);
  }, [gridCameras, captureVideoFrame]);

  const handleDismissFrozen = useCallback((slotIndex: number) => {
    setFrozenSlots(prev => {
      const next = new Map(prev);
      next.delete(slotIndex);
      return next;
    });
  }, []);

  // Intervalo que revisa si algún slot bajo demanda ha expirado → captura frame y congela
  useEffect(() => {
    const interval = setInterval(() => {
      const now = Date.now();
      slotPlacedAtRef.current.forEach((placedAt, slotIndex) => {
        if (now - placedAt >= ON_DEMAND_TIMEOUT_MS) {
          const cam = gridCameras[slotIndex];
          if (cam && isOnDemandCamera(cam)) {
            freezeSlot(slotIndex);
          }
        }
      });
    }, 10000);
    return () => clearInterval(interval);
  }, [gridCameras, isOnDemandCamera, freezeSlot]);

  const filteredSchemaTree = React.useMemo(() => {
    const q = schemaSearchQuery.trim().toLowerCase();
    if (!q) {
      return { buckets: schemaTree.buckets, camerasBySite: schemaTree.camerasBySite };
    }
    type ServiceKey = 'PVVC' | 'Privados' | 'EU' | 'Masivos';
    const buckets: Record<ServiceKey, { sites: Site[] }> = {
      PVVC: { sites: [] },
      Privados: { sites: [] },
      EU: { sites: [] },
      Masivos: { sites: [] },
    };
    (Object.keys(schemaTree.buckets) as ServiceKey[]).forEach((key) => {
      const bucket = schemaTree.buckets[key];
      bucket.sites.forEach((site) => {
        const sid = String(site.id);
        const cams = schemaTree.camerasBySite.get(sid) || [];
        const siteNameMatch = String(site.name).toLowerCase().includes(q);
        const hasMatchingCamera = cams.some(
          (c) =>
            String(c.name).toLowerCase().includes(q) ||
            String(c.description ?? '').toLowerCase().includes(q)
        );
        if (siteNameMatch || hasMatchingCamera) {
          buckets[key].sites.push(site);
        }
      });
    });
    return { buckets, camerasBySite: schemaTree.camerasBySite };
  }, [schemaTree, schemaSearchQuery]);

  // Al buscar, desplegar automáticamente servicios y sitios que tengan coincidencias para ver los hijos
  useEffect(() => {
    const q = schemaSearchQuery.trim();
    if (!q) return;

    const keys: Array<'PVVC' | 'Privados' | 'EU' | 'Masivos'> = ['PVVC', 'Privados', 'EU', 'Masivos'];
    let nextServices: Record<string, boolean> = {};
    let nextSites: Record<string, boolean> = {};

    keys.forEach((key) => {
      const bucket = filteredSchemaTree.buckets[key];
      if (bucket.sites.length > 0) {
        nextServices[key] = true;
        bucket.sites.forEach((site) => {
          nextSites[String(site.id)] = true;
        });
      }
    });

    if (Object.keys(nextServices).length > 0) {
      setExpandedServices((prev) => ({ ...prev, ...nextServices }));
    }
    if (Object.keys(nextSites).length > 0) {
      setExpandedSites((prev) => ({ ...prev, ...nextSites }));
    }
  }, [schemaSearchQuery, filteredSchemaTree]);

  const toggleService = (key: 'PVVC' | 'Privados' | 'EU' | 'Masivos') => {
    setExpandedServices((prev) => ({ ...prev, [key]: !(prev[key] ?? true) }));
  };

  const toggleSite = (siteId: string) => {
    setExpandedSites((prev) => ({ ...prev, [siteId]: !(prev[siteId] ?? false) }));
  };

  const addCameraToFirstEmptySlot = async (camera: Camera) => {
    const emptyIndex = gridCameras.findIndex((c) => c === null);
    if (emptyIndex < 0) {
      alert('No hay slots vacíos en la vista actual. Cambia la vista o remueve una cámara.');
      return;
    }
    const newGrid = [...gridCameras];
    newGrid[emptyIndex] = camera;
    setGridCameras(newGrid);
    markSlotPlaced(emptyIndex);
    await startCameraStream(camera, emptyIndex);
  };

  // Manejar eventos de WebSocket
  const handleStreamStatusChange = useCallback((event: { cameraId: string; status: string; timestamp: string }) => {
    // Solo loguear errores y reconexiones
    setStreamStatuses(prev => {
      const newMap = new Map(prev);
      const current = newMap.get(event.cameraId) || { status: 'stopped', reconnectAttempts: 0, isReconnecting: false };

      if (event.status === 'crashed') {
        // Usar el número de intento que viene del backend (si está disponible)
        // El backend envía reconnectAttempt cada vez que verifica que sigue crashed
        const backendAttempt = (event as any).reconnectAttempt || 0;
        const maxAttempts = (event as any).maxReconnectAttempts || 3;
        const hasReachedLimit = (event as any).hasReachedLimit || false;

        // Si el backend envía el número de intento, usarlo directamente
        // Si no, incrementar el contador local
        const newAttempts = backendAttempt > 0
          ? Math.min(backendAttempt, maxAttempts)
          : Math.min((current.reconnectAttempts || 0) + 1, maxAttempts);

        // Si se alcanzó el límite, marcar como error para mostrar mensaje apropiado
        const finalStatus = hasReachedLimit && newAttempts >= maxAttempts ? 'error' : 'crashed';

        newMap.set(event.cameraId, {
          status: finalStatus,
          reconnectAttempts: newAttempts,
          isReconnecting: newAttempts < maxAttempts && !hasReachedLimit, // Solo reconectar si no alcanzó el máximo
        });

        console.log(`[StreamsPage] Stream ${event.cameraId} ${finalStatus} (intento ${newAttempts}/${maxAttempts}) - backendAttempt=${backendAttempt}, hasReachedLimit=${hasReachedLimit}`);

        // Actualizar el estado de la cámara en la grilla para mostrar el error
        if (hasReachedLimit && newAttempts >= maxAttempts) {
          setGridCameras(prev => {
            const updated = [...prev];
            const cameraIndex = updated.findIndex(cam => cam?.id === event.cameraId);
            if (cameraIndex >= 0 && updated[cameraIndex]) {
              updated[cameraIndex] = {
                ...updated[cameraIndex]!,
                status: 'error', // Cambiar a error para mostrar mensaje apropiado
              };
            }
            return updated;
          });
        }

        // Si el backend indica reconexión automática, intentar reconectar después de un delay
        if ((event as any).autoReconnect && newAttempts < maxAttempts && !hasReachedLimit) {
          console.log(`[StreamsPage] Reconexión automática programada para ${event.cameraId}`);
          setTimeout(() => {
            handleReconnect(event.cameraId);
          }, 2000); // Esperar 2 segundos antes de reconectar
        }
      } else if (event.status === 'starting') {
        newMap.set(event.cameraId, {
          status: event.status,
          reconnectAttempts: current.reconnectAttempts,
          isReconnecting: current.reconnectAttempts > 0,
        });
      } else if (event.status === 'running') {
        newMap.set(event.cameraId, {
          status: event.status,
          reconnectAttempts: 0,
          isReconnecting: false,
        });

        // Actualización inmediata: marcar como active si hlsReady, sin side-effects
        setGridCameras(prev => {
          const cameraIndex = prev.findIndex(cam => cam?.id === event.cameraId);
          if (cameraIndex >= 0 && prev[cameraIndex] !== null) {
            const camera = prev[cameraIndex]!;
            const updated = [...prev];
            updated[cameraIndex] = {
              ...camera,
              status: (event as any).hlsReady ? 'active' : camera.status,
            };
            return updated;
          }
          return prev;
        });

        // Fetch de URLs fuera del state updater para evitar side-effects en React
        axios.get(`${API_URL}/cameras/${event.cameraId}/streams`, {
          headers: { Authorization: `Bearer ${token}` }
        }).then(response => {
          const streamUrls = response.data;
          const hlsReady = !!(event as any).hlsReady;
          const preferWebrtc = streamingMode === 'webrtc';
          const canUseHls = !!streamUrls?.hls && hlsReady;
          const canUseWebrtc = !!streamUrls?.webrtc;

          setGridCameras(prevGrid => {
            const updatedGrid = [...prevGrid];
            const idx = updatedGrid.findIndex(cam => cam?.id === event.cameraId);
            if (idx >= 0 && updatedGrid[idx]) {
              const prevCam = updatedGrid[idx]!;
              const prevStreamUrl = prevCam.streamUrl;

              const nextStreamUrl =
                pickLiveStreamUrl(preferWebrtc, streamUrls) ||
                prevStreamUrl;

              const hasPlayable = canUseHls || canUseWebrtc;
              updatedGrid[idx] = {
                ...prevCam,
                streamUrl: nextStreamUrl,
                streams: streamUrls,
                status: hasPlayable ? 'active' : 'starting',
              };
            }
            return updatedGrid;
          });
        }).catch(err => {
          console.error(`[StreamsPage] Error obteniendo URLs para ${event.cameraId}:`, err);
          setGridCameras(prevGrid => {
            const updatedGrid = [...prevGrid];
            const idx = updatedGrid.findIndex(cam => cam?.id === event.cameraId);
            if (idx >= 0 && updatedGrid[idx] && (updatedGrid[idx]!.streamUrl || updatedGrid[idx]!.streams)) {
              updatedGrid[idx] = {
                ...updatedGrid[idx]!,
                status: 'active',
              };
            }
            return updatedGrid;
          });
        });
      } else if (event.status === 'stopped') {
        // Si el estado actual es 'starting', ignorar 'stopped' (es un restart en curso)
        if (current.status === 'starting') {
          return newMap;
        }
        newMap.set(event.cameraId, {
          status: event.status,
          reconnectAttempts: 0,
          isReconnecting: false,
        });

        setGridCameras(prev => {
          const updated = [...prev];
          const cameraIndex = updated.findIndex(cam => cam?.id === event.cameraId);
          if (cameraIndex >= 0 && updated[cameraIndex]) {
            if (updated[cameraIndex]!.status === 'starting') return prev;
            updated[cameraIndex] = {
              ...updated[cameraIndex]!,
              status: 'inactive',
            };
          }
          return updated;
        });
      } else {
        // Otros estados
        newMap.set(event.cameraId, {
          status: event.status,
          reconnectAttempts: current.reconnectAttempts,
          isReconnecting: false,
        });
      }

      return newMap;
    });
  }, [token, streamingMode]);

  const handleStreamError = useCallback((event: { cameraId: string; error: string; timestamp: string }) => {
    console.error('[StreamsPage] Stream error:', event);
    setStreamStatuses(prev => {
      const newMap = new Map(prev);
      const current = newMap.get(event.cameraId) || { status: 'stopped', reconnectAttempts: 0, isReconnecting: false };
      // Solo incrementar si no ha alcanzado el máximo
      const newAttempts = current.reconnectAttempts < 3 ? current.reconnectAttempts + 1 : 3;
      newMap.set(event.cameraId, {
        status: 'crashed',
        reconnectAttempts: newAttempts,
        isReconnecting: newAttempts < 3, // Solo reconectar si no alcanzó el máximo
      });
      return newMap;
    });
  }, []);

  // Conectar WebSocket
  useStreamWebSocket({
    onStatusChange: handleStreamStatusChange,
    onError: handleStreamError,
    enabled: true,
  });

  // Restaurar grilla desde localStorage, salvo cuando la entrada viene de mapa / alerta:
  // si no, una restauración asíncrona puede terminar después del auto-fill y dejar la grilla en caché.
  useEffect(() => {
    loadSites();

    if (suppressNextGridRestoreRef.current) {
      suppressNextGridRestoreRef.current = false;
      return;
    }

    const gen = ++gridRestoreGenerationRef.current;

    const stateFromNav = location.state as {
      fromMap?: boolean;
      mapSessionId?: number;
      siteId?: number;
      cameraIds?: number[];
      siteIds?: number[];
    } | null;

    const sp = new URLSearchParams(location.search);
    const hasAlertNavigation =
      stateFromNav?.fromMap === true ||
      stateFromNav?.mapSessionId != null ||
      stateFromNav?.siteId != null ||
      (stateFromNav?.cameraIds && stateFromNav.cameraIds.length > 0) ||
      (stateFromNav?.siteIds && stateFromNav.siteIds.length > 0) ||
      Boolean(sp.get('siteId')) ||
      Boolean(sp.get('siteIds')) ||
      Boolean(sp.get('cameraIds'));

    if (hasAlertNavigation) {
      gridInitializedRef.current = true;
      const totalSlots = getTotalSlots(2);
      setGridCameras(Array(totalSlots).fill(null));
      return;
    }

    const raw = localStorage.getItem(STREAMS_GRID_STORAGE_KEY);
    let saved: { layout: GridLayoutType; cameraIds: (string | null)[] } | null = null;
    try {
      if (raw) saved = JSON.parse(raw);
    } catch {
      // ignore
    }

    if (saved?.layout && Array.isArray(saved.cameraIds) && saved.cameraIds.length > 0) {
      setGridLayout(saved.layout);
      const totalSlots = getTotalSlots(saved.layout);
      (async () => {
        try {
          const response = await axios.get(`${API_URL}/cameras`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          if (gen !== gridRestoreGenerationRef.current) return;
          const cameras: Camera[] = response.data || [];
          setAllCameras(cameras);
          const idMap = new Map(cameras.map((c) => [String(c.id), c]));
          const newGrid: (Camera | null)[] = Array(totalSlots).fill(null);
          saved!.cameraIds.slice(0, totalSlots).forEach((id, i) => {
            if (id != null) {
              const cam = idMap.get(String(id));
              if (cam) newGrid[i] = cam;
            }
          });
          if (gen !== gridRestoreGenerationRef.current) return;
          setGridCameras(newGrid);
          const restoreJobs: { camera: Camera; index: number }[] = [];
          for (let i = 0; i < newGrid.length; i++) {
            const camera = newGrid[i];
            if (camera) {
              markSlotPlaced(i);
              restoreJobs.push({ camera, index: i });
            }
          }
          if (gen !== gridRestoreGenerationRef.current) return;
          await startStreamsInParallel(restoreJobs, startCameraStream);
        } catch (err) {
          console.error('[StreamsPage] Error restaurando grilla desde localStorage:', err);
          if (gen === gridRestoreGenerationRef.current) {
            setGridCameras(Array(totalSlots).fill(null));
          }
        } finally {
          if (gen === gridRestoreGenerationRef.current) {
            gridInitializedRef.current = true;
          }
        }
      })();
    } else {
      const totalSlots = getTotalSlots(gridLayout);
      setGridCameras(Array(totalSlots).fill(null));
      gridInitializedRef.current = true;
    }
  }, [token, location.state, location.search]);

  // Persistir grilla en localStorage cuando cambie (tras la carga inicial)
  useEffect(() => {
    if (!gridInitializedRef.current) return;
    const totalSlots = getTotalSlots(gridLayout);
    const cameraIds = gridCameras
      .slice(0, totalSlots)
      .map((c) => (c ? c.id : null));
    if (cameraIds.every((id) => id === null)) {
      try {
        localStorage.removeItem(STREAMS_GRID_STORAGE_KEY);
      } catch {
        // ignore
      }
      return;
    }
    try {
      localStorage.setItem(
        STREAMS_GRID_STORAGE_KEY,
        JSON.stringify({ layout: gridLayout, cameraIds })
      );
    } catch {
      // ignore
    }
  }, [gridLayout, gridCameras]);

  // Auto-fill desde mapa: cuando se navega desde MapComponent con state { siteId, cameraIds } o { siteIds }
  // También soporta query params en la URL para apertura en nueva pestaña
  useEffect(() => {
    if (!token) return;

    const stateFromNav = location.state as {
      siteId?: number;
      siteName?: string;
      cameraIds?: number[];
      siteIds?: number[];
      fromMap?: boolean;
      mapSessionId?: number;
      /** Punto de la alerta (botón) para ordenar cámaras por cercanía */
      latitude?: number;
      longitude?: number;
      deviceId?: number;
      buttonAlertId?: number;
    } | null;

    const searchParams = new URLSearchParams(location.search);

    const mapParams = {
      siteId: stateFromNav?.siteId ?? (searchParams.get('siteId') ? Number(searchParams.get('siteId')) : undefined),
      siteName: stateFromNav?.siteName ?? searchParams.get('siteName') ?? undefined,
      cameraIds: stateFromNav?.cameraIds ?? (searchParams.get('cameraIds') ? searchParams.get('cameraIds')!.split(',').map(Number).filter(n => !Number.isNaN(n)) : undefined),
      siteIds: stateFromNav?.siteIds ?? (searchParams.get('siteIds') ? searchParams.get('siteIds')!.split(',').map(Number) : undefined),
      mapSessionId: stateFromNav?.mapSessionId,
      alertLat: stateFromNav?.latitude,
      alertLng: stateFromNav?.longitude,
      deviceId: stateFromNav?.deviceId,
      buttonAlertId: stateFromNav?.buttonAlertId,
    };

    // Permitir auto-fill con solo cameraIds (ej. desde popup de cámara en el mapa)
    const hasSiteContext = mapParams.siteId != null || (mapParams.siteIds && mapParams.siteIds.length > 0);
    const hasCameraContext = mapParams.cameraIds && mapParams.cameraIds.length > 0;
    if (!hasSiteContext && !hasCameraContext) return;

    const dedupeKey = [
      mapParams.mapSessionId ?? location.key ?? '',
      mapParams.siteId ?? '',
      (mapParams.siteIds || []).join(','),
      (mapParams.cameraIds || []).join(','),
      mapParams.alertLat ?? '',
      mapParams.alertLng ?? '',
      mapParams.deviceId ?? '',
    ].join('|');

    if (lastMapAutofillKeyRef.current === dedupeKey) return;
    lastMapAutofillKeyRef.current = dedupeKey;

    const autoFillFromMap = async () => {
      try {
        try {
          localStorage.removeItem(STREAMS_GRID_STORAGE_KEY);
        } catch {
          // ignore
        }

        const response = await axios.get(`${API_URL}/cameras`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const cameras: Camera[] = response.data || [];
        setAllCameras(cameras);
        setFilteredCameras(cameras);

        let camerasToPlace: Camera[] = [];

        if (mapParams.cameraIds && mapParams.cameraIds.length > 0) {
          const idSet = new Set(mapParams.cameraIds.map(id => String(id)));
          camerasToPlace = cameras.filter(c => idSet.has(String(c.id)));
        } else if (mapParams.siteId) {
          const sid = String(mapParams.siteId);
          camerasToPlace = cameras.filter(c => String(c.site?.id) === sid);
        } else if (mapParams.siteIds && mapParams.siteIds.length > 0) {
          const idSet = new Set(mapParams.siteIds.map(id => String(id)));
          camerasToPlace = cameras.filter(c => c.site?.id && idSet.has(String(c.site.id)));
        }

        if (camerasToPlace.length === 0) {
          console.warn('[StreamsPage] No se encontraron cámaras para los sitios seleccionados desde el mapa');
          return;
        }

        const alat = mapParams.alertLat;
        const alng = mapParams.alertLng;
        if (
          typeof alat === 'number' &&
          Number.isFinite(alat) &&
          typeof alng === 'number' &&
          Number.isFinite(alng) &&
          camerasToPlace.length > 1
        ) {
          camerasToPlace = sortCamerasByAlertProximity(camerasToPlace, alat, alng);
        }

        // Seleccionar el layout óptimo según la cantidad de cámaras
        const count = camerasToPlace.length;
        let bestLayout: GridLayoutType;
        if (count === 1) bestLayout = 1;
        else if (count <= 4) bestLayout = 2;
        else if (count <= 6) bestLayout = '3x2';
        else if (count <= 8) bestLayout = 'surveillance';
        else if (count <= 9) bestLayout = 3;
        else if (count <= 12) bestLayout = '4x3';
        else bestLayout = 4;

        setGridLayout(bestLayout);
        const totalSlots = getTotalSlots(bestLayout);
        const toPlace = camerasToPlace.slice(0, totalSlots);

        // Inicializar la grilla con las cámaras
        const newGrid: (Camera | null)[] = Array(totalSlots).fill(null);
        toPlace.forEach((cam, i) => {
          newGrid[i] = cam;
          markSlotPlaced(i);
        });
        setGridCameras(newGrid);
        suppressNextGridRestoreRef.current = true;

        await startStreamsInParallel(
          toPlace.map((cam, i) => ({ camera: cam, index: i })),
          startCameraStream,
        );
      } catch (error) {
        console.error('[StreamsPage] Error en auto-fill desde mapa:', error);
      }
    };

    void autoFillFromMap().then(() => {
      window.history.replaceState({}, document.title);
    });
  }, [token, location.state, location.search, location.key]);

  useEffect(() => {
    filterCameras();
  }, [allCameras, searchTerm, selectedSite, gridCameras]);

  // Cargar cámaras automáticamente cuando hay texto en el buscador
  useEffect(() => {
    if (searchTerm.trim() && allCameras.length === 0) {
      loadCameras();
    }
    // Mostrar/ocultar banner según si hay texto de búsqueda
    setShowCameraBanner(searchTerm.trim().length > 0);
  }, [searchTerm]);

  const loadCameras = async (force = false) => {
    // Sin force conserva el cache; al reabrir el sidebar se refresca el semáforo operativo.
    if (!force && allCameras.length > 0) return;

    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/cameras`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      // Cargar todas las cámaras con su información RTSP
      setAllCameras(response.data);
      setFilteredCameras(response.data);
    } catch (error) {
      console.error('Error cargando cámaras:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadSites = async () => {
    try {
      const response = await axios.get(`${API_URL}/sites`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setSites(response.data?.data ?? response.data ?? []);
    } catch (error) {
      console.error('Error cargando sitios:', error);
      setSites([]);
    }
  };

  const filterCameras = () => {
    let filtered = [...allCameras];

    // No excluir cámaras que están en la grilla: así se puede usar la misma cámara en varios slots
    // (arrastrando desde el menú a otro slot o desde un slot a otro).

    // Filtrar por búsqueda
    if (searchTerm) {
      filtered = filtered.filter(
        (camera) =>
          camera.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
          camera.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          camera.site?.name.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Filtrar por sitio
    if (selectedSite) {
      filtered = filtered.filter((camera) => camera.site?.id === selectedSite);
    }

    setFilteredCameras(filtered);
  };

  const toggleCameraSelection = (cameraId: string) => {
    setSelectedCameras(prev => 
      prev.includes(cameraId) 
        ? prev.filter(id => id !== cameraId)
        : [...prev, cameraId]
    );
  };

  const handleDragStart = (camera: Camera, e?: React.DragEvent, fromSchema?: boolean) => {
    // Drag desde el banner/lista o desde el sidebar de esquema
    setDraggedCamera(camera);
    setDraggedFromIndex(null);
    setDragSource('list');
    if (fromSchema) setIsDraggingFromSchema(true);
    if (e?.dataTransfer) {
      e.dataTransfer.setData('text/plain', camera.id);
      e.dataTransfer.effectAllowed = 'copy';
      e.dataTransfer.dropEffect = 'copy';
    }
  };

  const handleDragEnd = () => {
    setIsDraggingFromSchema(false);
    setDraggedSite(null);
  };

  const handleSiteDragStart = (site: Site, cameras: Camera[], e: React.DragEvent) => {
    setDraggedSite({ site, cameras });
    setDraggedCamera(null);
    setDraggedFromIndex(null);
    setDragSource('site');
    setIsDraggingFromSchema(true);
    if (e.dataTransfer) {
      e.dataTransfer.setData('text/plain', `site:${site.id}`);
      e.dataTransfer.effectAllowed = 'copy';
      e.dataTransfer.dropEffect = 'copy';
    }
  };

  const handleGridDragStart = (slotIndex: number) => {
    const camera = gridCameras[slotIndex];
    if (!camera) return;
    // Drag desde una celda de la grilla (reordenar / swap)
    setDraggedCamera(camera);
    setDraggedFromIndex(slotIndex);
    setDragSource('grid');
  };

  /** Doble clic en slot: mostrar en 1x1; segundo doble clic vuelve a la grilla. */
  const handleSlotDoubleClick = useCallback((index: number) => {
    setFocusedSlotIndex((prev) => (prev === index ? null : index));
  }, []);

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
  };

  // NOTA: el teardown del stream lo maneja el conteo de viewers (VideoCard, al desmontarse,
  // envía /streams/viewer/stop). NO llamamos a POST /cameras/:id/stop desde acciones por-cliente
  // (cerrar/reemplazar un slot), porque el path de MediaMTX es COMPARTIDO (nombre = ID de cámara):
  // borrarlo cortaría el stream para todos los clientes que vean la misma cámara. El backend
  // libera el path vía el watchdog del ViewerTracker cuando el contador de viewers llega a 0.

  const handleDrop = async (slotIndex: number) => {
    const totalSlots = getTotalSlots(gridLayout);

    // Si viene un sitio completo:
    //  - Si hay slots vacíos: llenarlos en orden desde el primer slot disponible (ignora el slot destino).
    //  - Si la matriz está llena: reemplazar desde el slot destino en adelante (con wrap), conservando el resto.
    if (dragSource === 'site' && draggedSite) {
      const { cameras } = draggedSite;
      setDraggedSite(null);
      setDragSource(null);
      setIsDraggingFromSchema(false);

      const currentGrid = gridCameras.length < totalSlots
        ? [...gridCameras, ...Array(totalSlots - gridCameras.length).fill(null)]
        : [...gridCameras];

      const emptyIndices = currentGrid
        .slice(0, totalSlots)
        .map((c, i) => (c === null ? i : -1))
        .filter(i => i >= 0);

      if (emptyIndices.length > 0) {
        // Hay slots vacíos: colocar las cámaras del sitio solo en esos slots
        const toPlace = cameras.slice(0, emptyIndices.length);
        const newGrid = [...currentGrid];
        toPlace.forEach((cam, i) => {
          newGrid[emptyIndices[i]] = cam;
          markSlotPlaced(emptyIndices[i]);
        });
        setGridCameras(newGrid);
        await startStreamsInParallel(
          toPlace.map((cam, i) => ({ camera: cam, index: emptyIndices[i] })),
          startCameraStream,
        );
      } else {
        // No hay slots vacíos: reemplazar desde el slot destino en adelante (incluyéndolo),
        // con wrap-around, SIN borrar toda la matriz. Se conservan los slots no reemplazados.
        const toPlace = cameras.slice(0, totalSlots);
        const targetIndices = toPlace.map((_, i) => (slotIndex + i) % totalSlots);
        const newGrid = [...currentGrid];
        toPlace.forEach((cam, i) => {
          const target = targetIndices[i];
          // La cámara reemplazada se detiene sola: su VideoCard se desmonta y envía /viewers/stop.
          clearSlotTimer(target);
          newGrid[target] = cam;
          markSlotPlaced(target);
        });
        setGridCameras(newGrid);
        await startStreamsInParallel(
          toPlace.map((cam, i) => ({ camera: cam, index: targetIndices[i] })),
          startCameraStream,
        );
      }
      return;
    }

    if (!draggedCamera) return;

    // Si viene de la grilla, hacemos move/swap y NO reiniciamos stream.
    if (dragSource === 'grid' && draggedFromIndex !== null) {
      if (draggedFromIndex === slotIndex) {
        setDraggedCamera(null);
        setDraggedFromIndex(null);
        setDragSource(null);
        setIsDraggingFromSchema(false);
        return;
      }

      const newGridCameras = [...gridCameras];
      const targetCamera = newGridCameras[slotIndex];
      newGridCameras[slotIndex] = draggedCamera;
      newGridCameras[draggedFromIndex] = targetCamera ?? null;

      // Intercambiar los timestamps de bajo demanda al hacer swap
      const tsFrom = slotPlacedAtRef.current.get(draggedFromIndex);
      const tsTo = slotPlacedAtRef.current.get(slotIndex);
      if (tsFrom != null) slotPlacedAtRef.current.set(slotIndex, tsFrom);
      else slotPlacedAtRef.current.delete(slotIndex);
      if (tsTo != null) slotPlacedAtRef.current.set(draggedFromIndex, tsTo);
      else slotPlacedAtRef.current.delete(draggedFromIndex);

      setGridCameras(newGridCameras);
      setDraggedCamera(null);
      setDraggedFromIndex(null);
      setDragSource(null);
      setIsDraggingFromSchema(false);
      return;
    }

    // Si viene del banner/lista o sidebar: colocar en el slot destino.
    // Si la cámara ya estaba en otro slot, quitarla de ahí (solo puede estar en un slot a la vez).
    const newGridCameras = [...gridCameras];
    const existingIndex = newGridCameras.findIndex(
      (c) => c !== null && c.id === draggedCamera.id
    );
    if (existingIndex >= 0 && existingIndex !== slotIndex) {
      clearSlotTimer(existingIndex);
      newGridCameras[existingIndex] = null;
    }
    newGridCameras[slotIndex] = draggedCamera;
    markSlotPlaced(slotIndex);
    setGridCameras(newGridCameras);
    setDraggedCamera(null);
    setDraggedFromIndex(null);
    setDragSource(null);
    setIsDraggingFromSchema(false);

    // Iniciar el stream automáticamente
    await startCameraStream(draggedCamera, slotIndex);
  };

  const removeCameraFromGrid = async (slotIndex: number) => {
    const camera = gridCameras[slotIndex];
    clearSlotTimer(slotIndex);

    // Al quitar la cámara del grid, su VideoCard se desmonta y envía /viewers/stop.
    // No forzamos el borrado del path (compartido) para no cortar el stream a otros clientes.

    const newGridCameras = [...gridCameras];
    newGridCameras[slotIndex] = null;
    setGridCameras(newGridCameras);

    setStreamStatuses(prev => {
      const newMap = new Map(prev);
      newMap.delete(camera?.id || '');
      return newMap;
    });
  };

  /** Quita todas las cámaras de la grilla en una sola acción (como hacer clic en × de cada una). */
  const clearAllCamerasFromGrid = () => {
    // Cada VideoCard se desmonta al vaciar el grid y envía su propio /viewers/stop.
    const totalSlots = getTotalSlots(gridLayout);
    setGridCameras(Array(totalSlots).fill(null));
    setStreamStatuses(new Map());
    slotPlacedAtRef.current.clear();
    setFrozenSlots(new Map());
  };

  const handleReconnect = async (cameraId: string) => {
    try {
      console.log(`[StreamsPage] 🔄 Reconectando stream para cámara ${cameraId}...`);

      // Resetear contador de intentos de reconexión
      setStreamStatuses(prev => {
        const newMap = new Map(prev);
        newMap.set(cameraId, {
          status: 'starting',
          reconnectAttempts: 0,
          isReconnecting: false,
        });
        return newMap;
      });

      // Marcar como starting pero NO limpiar streamUrl todavía
      // (evita enviar /viewers/stop prematuramente y permite al VideoCard
      //  mantener el frame actual mientras se reinicia el backend)
      setGridCameras(prev => {
        const updated = [...prev];
        const cameraIndex = updated.findIndex(cam => cam?.id === cameraId);
        if (cameraIndex >= 0 && updated[cameraIndex]) {
          updated[cameraIndex] = {
            ...updated[cameraIndex]!,
            status: 'starting',
          };
        }
        return updated;
      });

      // Llamar al endpoint de restart (el backend hace stop+start internamente)
      const response = await axios.post(
        `${API_URL}/cameras/${cameraId}/restart`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const streamUrls = response.data.streams || {};

      // Si no se recibieron URLs, obtenerlas directamente
      if (!streamUrls.hls && !streamUrls.webrtc && !streamUrls.rtsp) {
        try {
          const urlResponse = await axios.get(`${API_URL}/cameras/${cameraId}/streams`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          Object.assign(streamUrls, urlResponse.data);
        } catch {
          // WebSocket actualizará cuando el stream esté running
        }
      }

      // Ahora sí limpiar streamUrl para forzar reconexión WebRTC con la nueva sesión
      setGridCameras(prev => {
        const updated = [...prev];
        const cameraIndex = updated.findIndex(cam => cam?.id === cameraId);
        if (cameraIndex >= 0 && updated[cameraIndex]) {
          const preferWebrtc = streamingMode === 'webrtc';
          const newStreamUrl = pickLiveStreamUrl(preferWebrtc, streamUrls);
          updated[cameraIndex] = {
            ...updated[cameraIndex]!,
            streamUrl: undefined,
            streams: streamUrls,
            status: 'starting',
          };
          // Forzar la reconexión: primero limpiar, luego asignar la nueva URL en el siguiente tick
          if (newStreamUrl) {
            setTimeout(() => {
              setGridCameras(p => {
                const u = [...p];
                const idx = u.findIndex(c => c?.id === cameraId);
                if (idx >= 0 && u[idx]) {
                  u[idx] = { ...u[idx]!, streamUrl: newStreamUrl, streams: streamUrls, status: 'active' };
                }
                return u;
              });
            }, 100);
          }
        }
        return updated;
      });
    } catch (error: any) {
      console.error(`[StreamsPage] ❌ Error reconectando stream ${cameraId}:`, error);

      setGridCameras(prev => {
        const updated = [...prev];
        const cameraIndex = updated.findIndex(cam => cam?.id === cameraId);
        if (cameraIndex >= 0 && updated[cameraIndex]) {
          updated[cameraIndex] = { ...updated[cameraIndex]!, status: 'error' };
        }
        return updated;
      });

      setStreamStatuses(prev => {
        const newMap = new Map(prev);
        newMap.set(cameraId, { status: 'error', reconnectAttempts: 3, isReconnecting: false });
        return newMap;
      });
    }
  };

  const startCameraStream = async (camera: Camera, slotIndex: number) => {
    try {
      // Primero, actualizar la cámara en la grilla con estado "starting" (sin streamUrl todavía)
      // Esto permite que el VideoCard muestre "Cargando stream..." mientras espera
      setGridCameras(prev => {
        const updated = [...prev];
        updated[slotIndex] = {
          ...camera,
          streamUrl: undefined,
          streams: undefined,
          status: 'starting', // Marcar como "starting" mientras se inicia
        };
        return updated;
      });

      // Llamar al backend para iniciar el stream
      // El backend llamará al video-engine y retornará las URLs
      const response = await axios.post(
        `${API_URL}/cameras/${camera.id}/start`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );

      // Obtener las URLs de stream (HLS, WebRTC, RTSP) desde la respuesta del backend
      const streamUrls = response.data.streams || {};

      if (!streamUrls.hls && !streamUrls.webrtc && !streamUrls.rtsp) {
        console.error(`[StreamsPage] No se recibieron URLs de stream para ${camera.id}`);
        // No mostrar alert todavía, esperar a que el WebSocket notifique el estado
        // El WebSocket manejará la actualización cuando el stream esté "running"
        return;
      }

      // Actualizar la cámara en la grilla con las URLs de stream
      // El estado "running" vendrá del WebSocket cuando el stream esté realmente listo
      const preferWebrtc = streamingMode === 'webrtc';
      const updatedCamera = {
        ...camera,
        // Evitar 404 inmediato: si hay HLS, NO asignar streamUrl hasta que backend indique hlsReady.
        // (si no hay HLS, usar RTSP como fallback).
        streamUrl: pickLiveStreamUrl(preferWebrtc, {
          ...streamUrls,
          // Con HLS preferido aún no listo, no forzar m3u8 prematuro
          hls: preferWebrtc ? streamUrls.hls : undefined,
        }) ?? (preferWebrtc
          ? (streamUrls.webrtc || (streamUrls.hls ? undefined : streamUrls.rtsp))
          : (streamUrls.hls ? undefined : (streamUrls.webrtc || streamUrls.rtsp))),
        streams: streamUrls, // Guardar todas las URLs disponibles
        status: 'starting', // Mantener como "starting" hasta que WebSocket confirme "running"
      };

      setGridCameras(prev => {
        const updated = [...prev];
        updated[slotIndex] = updatedCamera;
        return updated;
      });
    } catch (error: any) {
      console.error('Error iniciando stream:', error);
      console.error('Detalles del error:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
      });

      // Si hay error, remover la cámara de la grilla o marcarla como error
      setGridCameras(prev => {
        const updated = [...prev];
        updated[slotIndex] = {
          ...(updated[slotIndex] || camera),
          status: 'error',
        };
        return updated;
      });

      // Mostrar error al usuario (no mostrar JSON crudo; si es "no stream available" → "SIN SEÑAL")
      const rawMsg = error.response?.data?.message ?? error.response?.data?.error ?? error.message ?? '';
      const isNoStream = typeof rawMsg === 'string' && /no stream is available/i.test(rawMsg);
      alert(`Error al iniciar el stream: ${isNoStream ? 'SIN SEÑAL' : rawMsg}`);
    }
  };

  const startCameraStreamRef = useRef(startCameraStream);
  startCameraStreamRef.current = startCameraStream;

  /**
   * Al ampliar la grilla, los slots que estaban fuera de vista se desmontaron y VideoCard cerró WebRTC,
   * pero gridCameras seguía guardando streamUrl/sesiones WHEP inválidas → al volver a mostrarlos el
   * reproductor intentaba reusar esa URL (carga infinita / "no stream available"). Aquí se limpian y
   * se vuelve a llamar a start en el mismo tick que el cambio de layout.
   */
  const applyGridLayout = useCallback((next: GridLayoutType) => {
    const prevTotal = getTotalSlots(gridLayoutRef.current);
    const nextTotal = getTotalSlots(next);
    const snapshot = gridCamerasRef.current;

    setGridLayout(next);

    if (nextTotal <= prevTotal) {
      return;
    }

    const extendedSnapshot =
      nextTotal > snapshot.length
        ? [...snapshot, ...Array(nextTotal - snapshot.length).fill(null)]
        : snapshot;

    const jobs: { index: number; camera: Camera }[] = [];
    for (let i = prevTotal; i < nextTotal; i++) {
      const c = extendedSnapshot[i];
      if (c) jobs.push({ index: i, camera: c });
    }

    setGridCameras((prev) => {
      let grid = prev;
      if (nextTotal > prev.length) {
        grid = [...prev, ...Array(nextTotal - prev.length).fill(null)];
      }
      if (jobs.length === 0) {
        return grid;
      }
      const cleared = [...grid];
      for (const { index } of jobs) {
        const cam = cleared[index];
        if (cam) {
          cleared[index] = {
            ...cam,
            streamUrl: undefined,
            streams: undefined,
            status: 'starting',
          };
        }
      }
      return cleared;
    });

    if (jobs.length > 0) {
      setStreamStatuses((prev) => {
        const m = new Map(prev);
        for (const { camera } of jobs) {
          m.set(camera.id, { status: 'starting', reconnectAttempts: 0, isReconnecting: false });
        }
        return m;
      });
      for (const { index, camera } of jobs) {
        void startCameraStreamRef.current(
          { ...camera, streamUrl: undefined, streams: undefined },
          index
        );
      }
    }
  }, []);

  const handleContinueWatching = useCallback((slotIndex: number) => {
    const frozen = frozenSlots.get(slotIndex);
    if (!frozen) return;
    setFrozenSlots(prev => {
      const next = new Map(prev);
      next.delete(slotIndex);
      return next;
    });
    const newGrid = [...gridCameras];
    newGrid[slotIndex] = frozen.camera;
    setGridCameras(newGrid);
    slotPlacedAtRef.current.set(slotIndex, Date.now());
    startCameraStreamRef.current(frozen.camera, slotIndex);
  }, [frozenSlots, gridCameras]);

  const schemaSidebarPortal = createPortal(
    <>
      {!isSchemaSidebarCollapsed && (
        <div
          className={`streams-schema-backdrop ${isDraggingFromSchema ? 'streams-schema-backdrop-drag-passthrough' : ''}`}
          aria-hidden="true"
        />
      )}

      {/* Local schema sidebar (floating overlay, only on this screen) */}
      <aside
        className={`streams-schema-sidebar ${isSchemaSidebarCollapsed ? 'collapsed' : 'open'}`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="streams-schema-header">
          <div className="streams-schema-title">
            <LayoutGrid size={20} className="streams-schema-title-icon" aria-hidden />
            <div className="streams-schema-title-text">Cámaras Disponibles</div>
          </div>
          <div className="streams-schema-search-wrap">
            <Search size={16} className="streams-schema-search-icon" aria-hidden />
            <input
              type="text"
              className="streams-schema-search"
              placeholder="Buscar cámara..."
              value={schemaSearchQuery}
              onChange={(e) => setSchemaSearchQuery(e.target.value)}
              aria-label="Buscar cámara"
            />
          </div>
        </div>

        <div className="streams-schema-body">
          {(['PVVC', 'Privados', 'EU', 'Masivos'] as const).map((svcKey) => {
            const svc = filteredSchemaTree.buckets[svcKey];
            const isOpen = expandedServices[svcKey] ?? true;
            const siteCount = svc.sites.length;
            const q = schemaSearchQuery.trim().toLowerCase();
            const camCount = svc.sites.reduce((acc, s) => {
              let cams = filteredSchemaTree.camerasBySite.get(String(s.id)) || [];
              if (q) {
                cams = cams.filter(
                  (c) =>
                    String(c.name).toLowerCase().includes(q) ||
                    String(c.description ?? '').toLowerCase().includes(q)
                );
              }
              return acc + cams.length;
            }, 0);

            return (
              <div key={svcKey} className={`schema-section schema-section-${svcKey.toLowerCase()}`}>
                <button type="button" className="schema-section-header" onClick={() => toggleService(svcKey)}>
                  <span className="schema-section-dot" aria-hidden />
                  <ChevronDown size={16} className={`schema-chevron ${isOpen ? 'open' : ''}`} />
                  <span className="schema-section-name">{svcKey}</span>
                  <span className="schema-section-count">{camCount}</span>
                </button>

                {isOpen && (
                  <div className="schema-section-content">
                    {svc.sites.map((site) => {
                      const sid = String(site.id);
                      const siteOpen = expandedSites[sid] ?? false;
                      const allSiteCams = filteredSchemaTree.camerasBySite.get(sid) || [];
                      let cams = allSiteCams;
                      if (schemaSearchQuery.trim()) {
                        const q = schemaSearchQuery.trim().toLowerCase();
                        cams = allSiteCams.filter(
                          (c) =>
                            String(c.name).toLowerCase().includes(q) ||
                            String(c.description ?? '').toLowerCase().includes(q)
                        );
                      }
                      const siteOpUi = siteOperationalUi(allSiteCams);
                      return (
                        <div key={sid} className="schema-site">
                          <button
                            type="button"
                            className={`schema-site-header schema-site-header--${siteOpUi.mod} ${cams.length > 0 ? 'schema-site-header-draggable' : ''}`}
                            onClick={() => toggleSite(sid)}
                            draggable={cams.length > 0}
                            onDragStart={cams.length > 0 ? (ev) => handleSiteDragStart(site, cams, ev) : undefined}
                            onDragEnd={handleDragEnd}
                            title={
                              cams.length > 0
                                ? `${siteOpUi.title} — Arrastra el sitio a la grilla para cargar todas sus cámaras`
                                : 'Sin cámaras para arrastrar'
                            }
                          >
                            <GripVertical size={14} className="schema-grip" aria-hidden />
                            <span className="schema-site-name">{site.name}</span>
                            <span className="schema-site-cam-count">
                              {siteOpUi.total === 0 ? (
                                '—'
                              ) : (
                                <>
                                  <span style={{ color: siteOpUi.countColor }}>{siteOpUi.online}</span>
                                  <span className="schema-site-cam-total">/{siteOpUi.total}</span>
                                </>
                              )}
                            </span>
                            <ChevronDown size={14} className={`schema-chevron ${siteOpen ? 'open' : ''}`} />
                          </button>

                          {siteOpen && (
                            <div className="schema-cameras">
                              {cams.length === 0 ? (
                                <div className="schema-empty">
                                  Sin cámaras cargadas
                                </div>
                              ) : (
                                cams.map((cam) => {
                                  const opUi = streamCameraOperationalUi(cam);
                                  return (
                                  <div
                                    key={cam.id}
                                    className={`schema-camera schema-camera--${opUi.mod}`}
                                    draggable
                                    onDragStart={(ev) => handleDragStart(cam, ev, true)}
                                    onDragEnd={handleDragEnd}
                                    onDoubleClick={() => addCameraToFirstEmptySlot(cam)}
                                    title={`${opUi.title} — Arrastra a la grilla o doble click para agregar`}
                                  >
                                    <span
                                      className={`schema-camera-status schema-camera-status--${opUi.mod}`}
                                      aria-label={opUi.label}
                                      title={opUi.label}
                                    />
                                    <GripVertical size={14} className="schema-grip" aria-hidden />
                                    <div className="schema-camera-info">
                                      <span className="schema-camera-name">{cam.name}</span>
                                      <span className="schema-camera-address">
                                        {cam.siteAddress || cam.site?.address || cam.site?.name || cam.description || 'Sin ubicación'}
                                      </span>
                                    </div>
                                    {opUi.mod === 'off' && (
                                      <AlertTriangle size={14} className="schema-camera-warn" aria-hidden />
                                    )}
                                    {cam.hasSiren && (
                                      <span
                                        className="schema-camera-siren"
                                        title="Bocina en el mismo poste"
                                        aria-label="Bocina en el mismo poste"
                                      >
                                        <Volume2 size={14} aria-hidden />
                                      </span>
                                    )}
                                    <span className="schema-camera-badge">{svcKey}</span>
                                  </div>
                                  );
                                })
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}

          {schemaSearchQuery.trim() &&
            (['PVVC', 'Privados', 'EU', 'Masivos'] as const).every(
              (k) => filteredSchemaTree.buckets[k].sites.length === 0
            ) && (
              <div className="schema-hint schema-no-results">
                No hay resultados para &quot;{schemaSearchQuery.trim()}&quot;
              </div>
            )}

          {allCameras.length === 0 && !schemaSearchQuery.trim() && (
            <div className="schema-hint">
              Tip: abre este panel para cargar cámaras. Doble click en una cámara para agregarla a la vista.
            </div>
          )}
        </div>
      </aside>

      <button
        type="button"
        className={`streams-schema-toggle ${isSchemaSidebarCollapsed ? 'collapsed' : 'open'}`}
        onClick={() => {
          setIsSchemaSidebarCollapsed((v) => {
            if (v) {
              window.dispatchEvent(new CustomEvent('collapse-main-sidebar'));
            }
            return !v;
          });
        }}
        aria-label={isSchemaSidebarCollapsed ? 'Expandir esquema' : 'Colapsar esquema'}
        title={isSchemaSidebarCollapsed ? 'Expandir esquema' : 'Colapsar esquema'}
      >
        {isSchemaSidebarCollapsed ? <ChevronLeft size={18} /> : <ChevronRight size={18} />}
      </button>
    </>,
    document.body
  );

  return (
    <div className={`streams-shell ${!isSchemaSidebarCollapsed ? 'schema-sidebar-open' : ''}`}>
      {schemaSidebarPortal}

      <div className="streams-content">
        {/* Una línea: Vista (izq) y buscador (der) */}
        <div className="streams-header">
          <div className="streams-header-left">
            <div className="layout-controls">
              <span className="layout-label">Vista:</span>
              <button
                className={`layout-btn ${gridLayout === 1 ? 'active' : ''}`}
                onClick={() => applyGridLayout(1)}
                title="1×1"
              >
                {renderLayoutIcon(1, 1)}
              </button>
              <button
                className={`layout-btn ${gridLayout === 2 ? 'active' : ''}`}
                onClick={() => applyGridLayout(2)}
                title="2×2"
              >
                {renderLayoutIcon(2, 2)}
              </button>
              <button
                className={`layout-btn ${gridLayout === '3x2' ? 'active' : ''}`}
                onClick={() => applyGridLayout('3x2')}
                title="3×2"
              >
                {renderLayoutIcon(3, 2)}
              </button>
              <button
                className={`layout-btn ${gridLayout === 3 ? 'active' : ''}`}
                onClick={() => applyGridLayout(3)}
                title="3×3"
              >
                {renderLayoutIcon(3, 3)}
              </button>
              <button
                className={`layout-btn ${gridLayout === '4x3' ? 'active' : ''}`}
                onClick={() => applyGridLayout('4x3')}
                title="4×3"
              >
                {renderLayoutIcon(4, 3)}
              </button>
              <button
                className={`layout-btn ${gridLayout === 4 ? 'active' : ''}`}
                onClick={() => applyGridLayout(4)}
                title="4×4"
              >
                {renderLayoutIcon(4, 4)}
              </button>
              <button
                className={`layout-btn ${gridLayout === 'surveillance' ? 'active' : ''}`}
                onClick={() => applyGridLayout('surveillance')}
                title="Vigilancia (1 principal + 3 + 4)"
              >
                {renderSurveillanceLayoutIcon()}
              </button>
              <button
                type="button"
                className="layout-btn layout-clear-grid-btn"
                onClick={clearAllCamerasFromGrid}
                disabled={!gridCameras.some(Boolean)}
                title="Limpiar grilla (quitar todas las cámaras)"
                aria-label="Limpiar grilla"
              >
                <Eraser size={18} strokeWidth={2} aria-hidden />
              </button>
            </div>
        </div>
          <div className="streams-header-right">
            <div className="search-container streams-header-search">
              <Search size={16} className="search-icon-inner" aria-hidden />
              <input
                type="text"
                placeholder="Barrio, localidad, sitio o dirección..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="search-input"
              />
            </div>
            <button
              type="button"
              className="streams-fullscreen-btn"
              onClick={() => setIsStreamingFullscreen((v) => !v)}
              title={isStreamingFullscreen ? 'Salir de pantalla completa' : 'Pantalla completa (ocultar menú y cabecera)'}
              aria-label={isStreamingFullscreen ? 'Salir de pantalla completa' : 'Pantalla completa'}
            >
              {isStreamingFullscreen ? (
                <Minimize2 size={20} aria-hidden />
              ) : (
                <Maximize2 size={20} aria-hidden />
              )}
            </button>
          </div>
        </div>

        {/* Recuadros */}
        <div className="streams-view-right">
      {/* Banner de cámaras disponibles */}
      {showCameraBanner && (
        <div className="cameras-banner">
          <div className="banner-header">
            <h3>
              {loading ? 'Cargando cámaras...' : `Cámaras disponibles (${filteredCameras.length})`}
            </h3>
            <button
              className="close-banner-btn"
              onClick={() => {
                setSearchTerm('');
                setShowCameraBanner(false);
              }}
              title="Cerrar"
            >
              ×
            </button>
          </div>
          {loading ? (
            <div className="loading-cameras">
              <XTAMSpinner variant="inline" message="Cargando cámaras..." />
            </div>
          ) : filteredCameras.length > 0 ? (
            <div className="cameras-list">
              {filteredCameras.map((camera) => {
                const opUi = streamCameraOperationalUi(camera);
                return (
                <div
                  key={camera.id}
                  className={`camera-item camera-item--${opUi.mod}`}
                  draggable
                  onDragStart={(ev) => handleDragStart(camera, ev)}
                  title={opUi.title}
                >
                  <span
                    className={`schema-camera-status schema-camera-status--${opUi.mod}`}
                    aria-label={opUi.label}
                  />
                  <div className="camera-item-icon"><Video size={20} /></div>
                  <div className="camera-item-info">
                    <div className="camera-item-name">{camera.name}</div>
                    <div className="camera-item-address">
                      {camera.siteAddress || camera.site?.name || camera.description || 'Sin ubicación'}
                    </div>
                  </div>
                  <div className={`camera-item-badge camera-item-badge--${opUi.mod}`}>
                    {opUi.label}
                  </div>
                </div>
                );
              })}
            </div>
          ) : (
            <div className="no-cameras-found">
              No se encontraron cámaras con "{searchTerm}"
            </div>
          )}
        </div>
      )}

      {/* Grilla siempre montada: al enfocar un slot solo se cambia la vista con CSS para que no se recargue el video */}
      <div
        className={`video-grid layout-${gridLayout}${focusedSlotIndex !== null ? ' video-grid-focused' : ''}`}
        onDoubleClick={focusedSlotIndex !== null ? () => setFocusedSlotIndex(null) : undefined}
        role={focusedSlotIndex !== null ? 'button' : undefined}
        tabIndex={focusedSlotIndex !== null ? 0 : undefined}
        title={focusedSlotIndex !== null ? 'Doble clic para volver a la grilla' : undefined}
        aria-label={focusedSlotIndex !== null ? 'Vista ampliada; doble clic para volver a la grilla' : undefined}
      >
        {Array.from({ length: getTotalSlots(gridLayout) }, (_, i) => gridCameras[i] ?? null).map((camera, index) => {
          const isFocused = focusedSlotIndex === index;
          const frozen = frozenSlots.get(index);

          if (camera) {
            return (
              <div
                key={`slot-${index}-${camera.id}`}
                className={`grid-slot${isFocused ? ' grid-slot-focused' : ''}`}
                data-slot-index={index}
                draggable
                onDragStart={() => handleGridDragStart(index)}
                onDragOver={handleDragOver}
                onDrop={() => handleDrop(index)}
                onDoubleClick={(e) => { e.stopPropagation(); handleSlotDoubleClick(index); }}
                title={isFocused ? 'Doble clic para volver a la grilla' : 'Doble clic para ver en pantalla completa'}
              >
                <VideoCard
                  camera={camera}
                  reconnectAttempts={streamStatuses.get(camera.id)?.reconnectAttempts || 0}
                  isReconnecting={streamStatuses.get(camera.id)?.isReconnecting || false}
                  onReconnect={handleReconnect}
                  onClose={() => removeCameraFromGrid(index)}
                />
                {isFocused && <div className="video-grid-focused-hint" aria-hidden>Doble clic para volver a la grilla</div>}
              </div>
            );
          }

          if (frozen) {
            const siteName = frozen.camera.site?.name || frozen.camera.name;
            const mins = Math.round(ON_DEMAND_TIMEOUT_MS / 60000);
            return (
              <div
                key={`frozen-${index}`}
                className={`grid-slot grid-slot--on-demand-frozen${isFocused ? ' grid-slot-focused' : ''}`}
                data-slot-index={index}
                onDragOver={handleDragOver}
                onDrop={() => handleDrop(index)}
              >
                {frozen.screenshot && (
                  <img
                    src={frozen.screenshot}
                    alt="Último frame"
                    className="on-demand-screenshot"
                  />
                )}
                <div className="on-demand-overlay">
                  <div className="on-demand-overlay-content">
                    <div className="on-demand-overlay-body">
                      <div className="on-demand-overlay-icon" aria-hidden>
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                          <circle cx="12" cy="12" r="10" />
                          <polyline points="12 6 12 12 16 14" />
                        </svg>
                      </div>
                      <span className="on-demand-overlay-site" title={siteName}>{siteName}</span>
                      <span className="on-demand-overlay-text">
                        Esta cámara funciona bajo demanda. Ya pasaron {mins} min de stream continuo.
                      </span>
                      <span className="on-demand-overlay-question">¿Desea reanudar la transmisión?</span>
                    </div>
                    <div className="on-demand-overlay-actions">
                      <button
                        type="button"
                        className="on-demand-btn on-demand-btn-yes"
                        onClick={(e) => { e.stopPropagation(); handleContinueWatching(index); }}
                      >
                        Reanudar
                      </button>
                      <button
                        type="button"
                        className="on-demand-btn on-demand-btn-no"
                        onClick={(e) => { e.stopPropagation(); handleDismissFrozen(index); }}
                      >
                        Cerrar
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            );
          }

          return (
            <div
              key={`empty-${index}`}
              className={`empty-slot${isFocused ? ' grid-slot-focused' : ''}`}
              data-slot-index={index}
              onDragOver={handleDragOver}
              onDrop={() => handleDrop(index)}
              onDoubleClick={(e) => { e.stopPropagation(); handleSlotDoubleClick(index); }}
              title={isFocused ? 'Doble clic para volver a la grilla' : 'Doble clic para ver en pantalla completa'}
            >
              <div className="empty-slot-content">
                <div className="empty-slot-icon" aria-hidden>
                  <svg viewBox="0 0 36 36" fill="currentColor">
                    <rect x="4" y="10" width="20" height="16" rx="3" opacity="0.55"/>
                    <path d="M24 14l8-5v18l-8-5z" opacity="0.55"/>
                  </svg>
                </div>
                <div className="empty-slot-text">Arrastra una cámara</div>
                <div className="empty-slot-number">Slot {index + 1}</div>
                {isFocused && <div className="empty-slot-hint">Doble clic para volver a la grilla</div>}
              </div>
            </div>
          );
        })}
      </div>
        </div>

      {/* Test streams info */}
      {testStreams.length > 0 && (
        <div className="test-streams-info">
          <h4>📊 Estado de Test Streams</h4>
          <div className="streams-list">
            {testStreams.map(stream => (
              <div key={stream.cameraId} className={`stream-item ${stream.status}`}>
                <span className="stream-name">{stream.cameraId}</span>
                <span className={`stream-status ${stream.status}`}>
                  {stream.status === 'active' ? '● Activo' : '○ Inactivo'}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
      </div>
    </div>
  );
};

export default StreamsPage;
